import React, { memo, useMemo, useCallback } from "react";
import cx from "classnames";
import { Icon } from "components/Icon/Icon";
import { Button, TranslateWrapper } from "components";
import { UncontrolledTooltip } from "reactstrap";
import NiceModal from "@ebay/nice-modal-react";
import { ProjectOrFolderDownloadModal } from "components/_Modals/ProjectDownloadModal/ProjectOrFolderDownloadModal";
import * as identify from "utils/treeUtils";
import {
  useAddedProductStore,
  useCopiedItemStore,
  useProjectDataStore,
} from "stores/projectStore";
import { WizardIndicator } from "./WizardIndicator";
import { useTranslate } from "hooks";
import { disableDrop } from "../utils";
import s from "./Styles.module.scss";

export const NodeLayout = memo(
  ({
    node,
    children,
    options,
    hasChildren,
    isOpen,
    onClick,
    isAncestorDragging,
    isDragging,
    willReceiveDrop,
    onDoubleClick,
    isPasteAllowed,
    dragNode,
    isActiveProductNode,
  }) => {
    const translate = useTranslate();
    const projectId = useProjectDataStore((state) => state.projectId);
    const projectTitle = useProjectDataStore((state) => state.projectTitle);
    const copiedItem = useCopiedItemStore((state) => state.copiedItem);
    const addedProducts = useAddedProductStore((state) => state.addedProducts);
    const { data, index } = node;

    const isWizard = identify.isWizard(data);
    const isFolder = identify.isFolder(data);
    const isRoot = identify.isRoot(data);
    const isAnyProduct = identify.isAnyProduct(data);
    const isAnyFolder = identify.isAnyFolder(data);
    const isTroxNetcom = identify.isTroxNetcom(data);
    const isAccessoryProduct = identify.isProductAccessory(data);
    const itemToCompare = copiedItem || dragNode;

    const baseBlurCheck =
      isAncestorDragging ||
      (itemToCompare &&
        disableDrop({
          parentNode: node,
          dragNodes: [itemToCompare],
          index,
        }));

    const isBlurred = baseBlurCheck && !addedProducts.includes(data.id);

    const isActionAllowed = (!isBlurred && copiedItem) || !copiedItem;

    const dataTestID = useMemo(() => {
      const nodeProps = children.props.node.data;
      if (isAnyProduct) {
        return `product-${nodeProps.presentation}`;
      }
      return isWizard
        ? `wizard-${nodeProps.folderName?.replaceAll(" ", "")}`
        : `folder-${nodeProps.folderName?.replaceAll(" ", "")}`;
    }, [children.props.node.data, isAnyProduct, isWizard]);

    const openDownloadModal = useCallback(() => {
      NiceModal.show(ProjectOrFolderDownloadModal, {
        modalSubtitle: projectTitle,
        projectId,
      });
    }, [projectId, projectTitle]);

    return (
      <div
        className="w-100 h-100 py-2 position-relative text-gray-700"
        style={{ minWidth: "180px" }}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        data-testid={`ProjectTree_${dataTestID}`}
      >
        <div
          className={cx(
            "w-100 d-flex flex-row justify-content-between align-items-center rounded-2 border h-100",
            {
              [s.folder]: isAnyFolder,
              [s.configuration]: isAnyProduct,
              "border-info":
                willReceiveDrop && !isDragging && !isAncestorDragging,
              [s.disabledFolder]: isAnyFolder && isBlurred,
              "opacity-50": !isAnyFolder && isBlurred,
              "opacity-25": isDragging,
              [s.pastePreview]: !isBlurred && copiedItem && isPasteAllowed,
              "cursor-pointer": isActionAllowed,
              "cursor-not-allowed": !isActionAllowed,
              "border-info bg-blue-50": isActiveProductNode,
            },
          )}
        >
          <LeftSideComponents
            isWizard={isWizard}
            isTroxNetcom={isTroxNetcom}
            isProduct={identify.isProduct(data)}
            isCustomProduct={identify.isCustomProduct(data)}
            isAnyProduct={isAnyProduct}
            isAccessoryProduct={isAccessoryProduct}
            isFolder={isFolder}
            isRoot={isRoot}
            options={options}
          />
          <div
            className={cx(
              "d-flex flex-row justify-content-between align-items-center pe-4 w-100 h-100 overflow-hidden",
              {
                "ps-1": isRoot || (options.dndDisabled && !isWizard),
              },
            )}
          >
            {children}

            {isRoot && !isWizard && isActionAllowed && options?.isFullpage && (
              <>
                <Button
                  data-testid="NodeLayout_download-btn"
                  color="transparent"
                  className="px-2 border-0"
                  onClick={openDownloadModal}
                  id="NodeLayout_project_item_download_icon"
                >
                  <Icon
                    prefix="trox"
                    iconName="arrow-down-bracket-light"
                    className={cx("icon-16", s.iconButton)}
                  />
                </Button>
                <UncontrolledTooltip
                  target="NodeLayout_project_item_download_icon"
                  placement="top"
                >
                  {translate("DOWNLOAD")}
                </UncontrolledTooltip>
                {typeof translate("DOWNLOAD") === "object" && (
                  <TranslateWrapper
                    tooltipText="DOWNLOAD"
                    tooltipId="NodeLayout_project_item_download_icon"
                  />
                )}
              </>
            )}
          </div>
        </div>
        <BottomSpacer
          options={options}
          hasChildren={hasChildren}
          isOpen={isOpen}
        />
      </div>
    );
  },
);
const LeftSideComponents = memo(
  ({
    isWizard,
    isTroxNetcom,
    isAnyProduct,
    isRootNode,
    isFolder,
    isProduct,
    isCustomProduct,
    isAccessoryProduct,
    options,
  }) => (
    <>
      {(isWizard || isTroxNetcom) && (
        <div
          className={cx("h-100", {
            "pe-2": isAnyProduct,
          })}
        >
          <WizardIndicator />
        </div>
      )}
      {isAccessoryProduct && <div className="pe-3" />}
      {!isRootNode &&
        (isProduct || isFolder || isCustomProduct) &&
        !options.dndDisabled && (
          <>
            <div
              className={cx("", {
                "ps-1": !isAnyProduct,
                "ps-3 pe-2": isAnyProduct,
              })}
            >
              <Icon
                iconName="grip-dots-vertical"
                className={cx("icon-12 cursor-pointer", {
                  "text-gray-400": isAnyProduct,
                  "text-gray-700": !isAnyProduct,
                })}
              />
            </div>
          </>
        )}
    </>
  ),
);

export const BottomSpacer = memo(({ options, hasChildren, isOpen }) => (
  <>
    {options.isIndentCompact ? (
      <CompactSpacer
        width={options.indentSize}
        hasChildren={hasChildren}
        isOpen={isOpen}
      />
    ) : (
      <Spacer
        width={options.indentSize}
        hasChildren={hasChildren}
        isOpen={isOpen}
      />
    )}
  </>
));

const Spacer = ({ width, hasChildren, isOpen }) => (
  <div className={cx("w-100 d-flex flex-row", s.bottomSpacer)}>
    <div className={cx("h-100 d-flex flex-row")} style={{ width }}>
      <div className="w-50 h-100"></div>
      <div
        className={cx("w-50 h-100 border-gray-400", {
          "border-start": hasChildren && isOpen,
        })}
      ></div>
    </div>
    <div className="h-100 flex-grow-1"></div>
  </div>
);

const CompactSpacer = ({ width, hasChildren, isOpen }) => (
  <div className={cx("w-100 d-flex flex-row", s.bottomSpacer)}>
    <div
      className={cx("w-100 h-100 border-gray-400", {
        "border-start": hasChildren && isOpen,
      })}
      style={{ width }}
    ></div>
  </div>
);
